import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme";
import ErrorBoundary from "@/components/ErrorBoundary";
import SuspenseFallback from "@/components/SuspenseFallback";
import { lazyWithDelay } from "@/lib/lazyWithDelay";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PlanGate from "./components/auth/PlanGate";
import AuthCacheGuard from "./components/auth/AuthCacheGuard";
import ScrollToTop from "./components/ScrollToTop";
import RoutePrefetcher from "./components/RoutePrefetcher";

// Public pages — lazy-loaded so they don't bloat the landing-page bundle.
// (Index and NotFound stay eager: Index is the LCP page, NotFound is tiny.)
const Login = lazyWithDelay(() => import("./pages/Login"));
const Cadastro = lazyWithDelay(() => import("./pages/Cadastro"));
const ReferralRedirect = lazyWithDelay(() => import("./pages/ReferralRedirect"));
const ValidarEmbaixador = lazyWithDelay(() => import("./pages/ValidarEmbaixador"));
const RecuperarSenha = lazyWithDelay(() => import("./pages/RecuperarSenha"));
const RedefinirSenha = lazyWithDelay(() => import("./pages/RedefinirSenha"));
const EmailConfirmado = lazyWithDelay(() => import("./pages/EmailConfirmado"));
const FAQ = lazyWithDelay(() => import("./pages/FAQ"));
const TermosDeUso = lazyWithDelay(() => import("./pages/TermosDeUso"));
const Privacidade = lazyWithDelay(() => import("./pages/Privacidade"));
const PoliticaCookies = lazyWithDelay(() => import("./pages/PoliticaCookies"));
const Blog = lazyWithDelay(() => import("./pages/Blog"));
const BlogPost = lazyWithDelay(() => import("./pages/BlogPost"));
const Incorporadoras = lazyWithDelay(() => import("./pages/Incorporadoras"));

// Authenticated pages (already lazy)
const PagamentoPendente = lazyWithDelay(() => import("./pages/PagamentoPendente"));
const PreCheckout = lazyWithDelay(() => import("./pages/PreCheckout"));
const Dashboard = lazyWithDelay(() => import("./pages/Dashboard"));

// Partner pages (lazy)
const PartnerClientes = lazyWithDelay(() => import("./pages/partner/PartnerClientes"));
const PartnerOportunidades = lazyWithDelay(() => import("./pages/partner/PartnerOportunidades"));
const PartnerComissoes = lazyWithDelay(() => import("./pages/partner/PartnerComissoes"));
const PartnerMeusDados = lazyWithDelay(() => import("./pages/partner/PartnerMeusDados"));
const PartnerCRM = lazyWithDelay(() => import("./pages/partner/PartnerCRM"));
const PartnerKanban = lazyWithDelay(() => import("./pages/partner/PartnerKanban"));
const PartnerAtendimento = lazyWithDelay(() => import("./pages/partner/PartnerAtendimento"));
const PartnerOpportunityDetails = lazyWithDelay(() => import("./pages/partner/PartnerOpportunityDetails"));
const PartnerAssinatura = lazyWithDelay(() => import("./pages/partner/PartnerAssinatura"));
const PartnerSimulador = lazyWithDelay(() => import("./pages/partner/PartnerSimulador"));
const PartnerSimuladorObra = lazyWithDelay(() => import("./pages/partner/PartnerSimuladorObra"));
const PartnerSimuladorMCMV = lazyWithDelay(() => import("./pages/partner/PartnerSimuladorMCMV"));
const PartnerPrivacidade = lazyWithDelay(() => import("./pages/partner/PartnerPrivacidade"));
const PartnerIndicacoes = lazyWithDelay(() => import("./pages/partner/PartnerIndicacoes"));
const PartnerMarketing = lazyWithDelay(() => import("./pages/partner/PartnerMarketing"));
const PartnerEquipe = lazyWithDelay(() => import("./pages/partner/PartnerEquipe"));
const PartnerComecarAqui = lazyWithDelay(() => import("./pages/PartnerComecarAqui"));

// Admin pages (lazy)
const AdminDashboard = lazyWithDelay(() => import("./pages/admin/AdminDashboard"));
const AdminParceiros = lazyWithDelay(() => import("./pages/admin/AdminParceiros"));
const AdminPartnerDetails = lazyWithDelay(() => import("./pages/admin/AdminPartnerDetails"));
const AdminOportunidades = lazyWithDelay(() => import("./pages/admin/AdminOportunidades"));
const AdminComissoes = lazyWithDelay(() => import("./pages/admin/AdminComissoes"));
const AdminServicos = lazyWithDelay(() => import("./pages/admin/AdminServicos"));
const AdminServiceDetails = lazyWithDelay(() => import("./pages/admin/AdminServiceDetails"));
const AdminMetas = lazyWithDelay(() => import("./pages/admin/AdminMetas"));
const AdminRelatorioComissoes = lazyWithDelay(() => import("./pages/admin/AdminRelatorioComissoes"));
const AdminPlanos = lazyWithDelay(() => import("./pages/admin/AdminPlanos"));
const AdminAuditoria = lazyWithDelay(() => import("./pages/admin/AdminAuditoria"));
const AdminIntegracoes = lazyWithDelay(() => import("./pages/admin/AdminIntegracoes"));
const AdminHubfiWebhooks = lazyWithDelay(() => import("./pages/admin/AdminHubfiWebhooks"));
const AdminPermissoes = lazyWithDelay(() => import("./pages/admin/AdminPermissoes"));
const AdminAtendimento = lazyWithDelay(() => import("./pages/admin/AdminAtendimento"));
const AdminSimulador = lazyWithDelay(() => import("./pages/admin/AdminSimulador"));
const AdminSimuladorMCMV = lazyWithDelay(() => import("./pages/admin/AdminSimuladorMCMV"));
const AdminSimuladorObra = lazyWithDelay(() => import("./pages/admin/AdminSimuladorObra"));
const AdminSimulacoes = lazyWithDelay(() => import("./pages/admin/AdminSimulacoes"));
const AdminPrivacidade = lazyWithDelay(() => import("./pages/admin/AdminPrivacidade"));
const AdminFinanceiroDRE = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroDRE"));
const AdminFinanceiroCashFlow = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroCashFlow"));
const AdminFinanceiroUnitEconomics = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroUnitEconomics"));
const AdminFinanceiroAging = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroAging"));
const AdminFinanceiroCaixa = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroCaixa"));
const AdminFinanceiroIndicacoes = lazyWithDelay(() => import("./pages/admin/AdminFinanceiroIndicacoes"));
const AdminMarketing = lazyWithDelay(() => import("./pages/admin/AdminMarketing"));
const AdminAcervoPosts = lazyWithDelay(() => import("./pages/admin/AdminAcervoPosts"));
const AdminIncorporadoraLeads = lazyWithDelay(() => import("./pages/admin/AdminIncorporadoraLeads"));
const AdminIncorporadoraCarteiras = lazyWithDelay(() => import("./pages/admin/AdminIncorporadoraCarteiras"));
const AdminIncorporadoraMembros = lazyWithDelay(() => import("./pages/admin/AdminIncorporadoraMembros"));

// Incorporadora portal (lazy)
const IncorporadoraDashboard = lazyWithDelay(() => import("./pages/incorporadora/IncorporadoraDashboard"));
const IncorporadoraCarteirasPortal = lazyWithDelay(() => import("./pages/incorporadora/IncorporadoraCarteiras"));
const IncorporadoraEquipePortal = lazyWithDelay(() => import("./pages/incorporadora/IncorporadoraEquipe"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000, // 24h - necessário para persistência funcionar
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const persister = typeof window !== "undefined"
  ? createSyncStoragePersister({
      storage: window.localStorage,
      key: "acelera-query-cache",
    })
  : undefined;

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister: persister!,
      maxAge: 24 * 60 * 60 * 1000,
      buster: "v1",
    }}
  >
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
      <ErrorBoundary>
      <BrowserRouter>
        <AuthCacheGuard />
        <ScrollToTop />
        <RoutePrefetcher />
        <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Index />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/termos" element={<TermosDeUso />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/cookies" element={<PoliticaCookies />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/incorporadoras" element={<Incorporadoras />} />
          <Route path="/cadastro" element={<Cadastro />} />
          <Route path="/r/:code" element={<ReferralRedirect />} />
          <Route path="/embaixador/:code" element={<ValidarEmbaixador />} />
          <Route path="/pre-checkout" element={<PreCheckout />} />
          <Route path="/login" element={<Login />} />
          <Route path="/recuperar-senha" element={<RecuperarSenha />} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/email-confirmado" element={<EmailConfirmado />} />
          <Route path="/pagamento-pendente" element={
            <ProtectedRoute requiredRole="partner">
              <PagamentoPendente />
            </ProtectedRoute>
          } />

          {/* Partner routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute requiredRole="partner">
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/comecar" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerComecarAqui />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/clientes" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerClientes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/oportunidades" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerOportunidades />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/oportunidades/:opportunityId" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerOpportunityDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/comissoes" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerComissoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/indicacoes" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerIndicacoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/meus-dados" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerMeusDados />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/crm" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PlanGate requiredPlan="pro" featureName="CRM Avançado">
                  <PartnerCRM />
                </PlanGate>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/kanban" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PlanGate requiredPlan="pro" featureName="Funil de Vendas">
                  <PartnerKanban />
                </PlanGate>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/atendimento" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerAtendimento />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/simulador" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerSimulador />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/simulador/obra" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerSimuladorObra />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/simulador/mcmv" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerSimuladorMCMV />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/assinatura" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerAssinatura />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/privacidade" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerPrivacidade />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/marketing" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerMarketing />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/equipe" 
            element={
              <ProtectedRoute requiredRole="partner">
                <PartnerEquipe />
              </ProtectedRoute>
            } 
          />

          {/* Admin routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/parceiros" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminParceiros />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/parceiros/:partnerId" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPartnerDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/oportunidades" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminOportunidades />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/comissoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminComissoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/servicos" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminServicos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/servicos/:serviceId" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminServiceDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/metas" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminMetas />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/relatorio-comissoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminRelatorioComissoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planos" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPlanos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/auditoria" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAuditoria />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/integracoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminIntegracoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/webhooks-hubfi" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminHubfiWebhooks />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/permissoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPermissoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/atendimento" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAtendimento />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/marketing" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminMarketing />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/acervo-posts" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAcervoPosts />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/simulador" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSimulador />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/simulador/mcmv" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSimuladorMCMV />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/simulador/obra" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSimuladorObra />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/simulacoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSimulacoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/privacidade" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPrivacidade />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/dre" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroDRE />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/cashflow" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroCashFlow />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/unit-economics" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroUnitEconomics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/caixa" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroCaixa />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/aging" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroAging />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/financeiro/indicacoes" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminFinanceiroIndicacoes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/incorporadoras" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminIncorporadoraLeads />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/incorporadoras/carteiras" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminIncorporadoraCarteiras />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/incorporadoras/membros" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminIncorporadoraMembros />
              </ProtectedRoute>
            } 
          />

          {/* Incorporadora portal */}
          <Route path="/incorporadora" element={
            <ProtectedRoute requiredRole="incorporadora"><IncorporadoraDashboard /></ProtectedRoute>
          } />
          <Route path="/incorporadora/carteiras" element={
            <ProtectedRoute requiredRole="incorporadora"><IncorporadoraCarteirasPortal /></ProtectedRoute>
          } />
          <Route path="/incorporadora/equipe" element={
            <ProtectedRoute requiredRole="incorporadora"><IncorporadoraEquipePortal /></ProtectedRoute>
          } />


          {/* Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        
      </BrowserRouter>
      </ErrorBoundary>
      </TooltipProvider>
    </ThemeProvider>
  </PersistQueryClientProvider>
);

export default App;
